// Hook TanStack Query — le passage du soir (GET /api/environnements/passage), rafraichi toutes les 5 minutes.
//
// FORME : recopiee du contrat serveur `apps/admin/server/services/passageDuSoir/contrat.ts`,
// qui la VALIDE par Zod a la sortie. Recopiee et non importee : le projet client a
// `rootDir: ./src` (meme raison que useEnvironnements.ts). Un champ ajoute au contrat s ajoute ici.
//
// LECTURE SEULE : aucune mutation n ecrit ; « Remesurer » relit. L approbation se fait sur GitHub.
// Cle de cache SANS companyId, et c est voulu (ADR-032) : etat de la PLATEFORME.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';

export type CodeDeMotif =
  | 'non-configure'
  | 'identite'
  | 'coffre-refuse'
  | 'acces-refuse'
  | 'delai'
  | 'reponse-invalide'
  | 'erreur-github';
export interface MotifGithub {
  code: CodeDeMotif;
  http: number | null;
}

export type Lecture<T> = { etat: 'lu'; valeur: T } | { etat: 'illisible'; motif: MotifGithub };

export interface Passage {
  id: number;
  lien: string;
  creeA: string;
  declencheur: string;
  statut: string;
  conclusion: string | null;
}

export type Verification = 'reussie' | 'echouee' | 'en-cours' | 'autre';

export interface AppDuPassage {
  cle: string | null;
  nom: string;
  verification: Verification;
  versionProduction: string | null;
  retard: number | null;
}

export interface Fusion {
  numero: number | null;
  titre: string;
  sha: string;
  date: string | null;
}

export interface Changements {
  depuis: string;
  total: number;
  partiel: boolean;
  fusions: Fusion[];
}

export interface PassageEnAttente {
  passage: Passage;
  tete: string;
  portes: Lecture<{ environnement: string; depuis: string | null }[]>;
  apps: Lecture<AppDuPassage[]>;
  changements: Lecture<Changements | null>;
}

export interface PassageDuSoir {
  mesureA: string;
  arme: Lecture<boolean>;
  flux: Lecture<{ existe: boolean; lien: string }>;
  dernier: Lecture<Passage | null>;
  enAttente: Lecture<PassageEnAttente[]>;
}

export const PASSAGE_QUERY_KEY = ['passage-du-soir'] as const;
const CINQ_MINUTES_MS = 5 * 60_000;

export function usePassageDuSoir() {
  const { user } = useAuth();
  return useQuery({
    queryKey: PASSAGE_QUERY_KEY,
    queryFn: () => api.get<PassageDuSoir>('/api/environnements/passage'),
    enabled: !!user,
    refetchInterval: CINQ_MINUTES_MS,
    staleTime: CINQ_MINUTES_MS,
  });
}

/** « Remesurer » : le serveur ne relit GitHub que si sa lecture a plus d une minute. */
export function useRemesurerPassage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<PassageDuSoir>('/api/environnements/passage?remesurer=1'),
    onSuccess: (donnees) => client.setQueryData(PASSAGE_QUERY_KEY, donnees),
  });
}
